import { Intent } from 'enums';
import { NavLink as BaseNavLink, Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { Active, Color, IHrefLink, IntentProps } from './types';

interface StyledComponentProps extends Color {
  theme: any;
}

const options = (props: StyledComponentProps) => ({
  color: props.$color ? props.$color : 'steel',
});

/**
 * PRIMARY intent
 */
const intentPrimary = css<Active>`
  color: ${(props) => props.theme.intent.primary.foreground};

  ${(props) =>
    props.$active &&
    css`
      :link,
      :visited,
      :active {
        color: ${props.theme.colors.blueDark};
      }
    `}

  &:hover {
    color: ${(props) => props.theme.intent.primary.hover};
  }
`;

/**
 * SECONDARY intent
 */
const intentSecondary = css<Active>`
  color: ${(props) => props.theme.intent.secondary.foreground};

  ${(props) =>
    props.$active &&
    css`
      :link,
      :visited,
      :active {
        color: ${props.theme.colors.blue};
      }
    `}

  &:hover {
    color: ${(props) => props.theme.colors.blue};
  }
`;

/**
 * SUCCESS intent
 */
const intentSuccess = css<Active>`
  color: ${(props) => props.theme.intent.success.foreground};

  ${(props) =>
    props.$active &&
    css`
      :link,
      :visited,
      :active {
        color: ${props.theme.colors.softGreen};
      }
    `}

  &:hover {
    color: ${(props) => props.theme.intent.success.hover};
  }
`;

/**
 * WARNING intent
 */
const intentWarning = css<Active>`
  color: ${(props) => props.theme.intent.warning.foreground};

  ${(props) =>
    props.$active &&
    css`
      :link,
      :visited,
      :active {
        color: ${props.theme.colors.black};
      }
    `}

  &:hover {
    color: ${(props) => props.theme.intent.warning.hover};
  }
`;

/**
 * DANGER intent
 */
const intentDanger = css<Active>`
  color: ${(props) => props.theme.intent.danger.background};

  ${(props) =>
    props.$active &&
    css`
      :link,
      :visited,
      :active {
        color: ${props.theme.colors.deepRose};
      }
    `}

  &:hover {
    color: ${(props) => props.theme.intent.danger.hover};
  }
`;

const base = css<Color & Active & IntentProps>`
  font-weight: 500;
  line-height: normal;
  display: inline-flex;
  cursor: pointer;

  :link,
  :visited,
  :active {
    color: ${(props) => props.$color || props.theme.colors.steel};
  }

  ${(props) =>
    props.$active &&
    css`
      color: ${props.theme.colors.blueDark};
      :link,
      :visited,
      :active {
        color: ${props.theme.colors.blueDark};
      }
    `}

  &:hover {
    color: ${(props) => props.theme.colors.blue};
  }

  ${(props) =>
    props.$intent === Intent.PRIMARY &&
    css`
      ${intentPrimary}
    `}

  ${(props) =>
    props.$intent === Intent.SECONDARY &&
    css`
      ${intentSecondary}
    `}

  ${(props) =>
    props.$intent === Intent.SUCCESS &&
    css`
      ${intentSuccess}
    `}

  ${(props) =>
    props.$intent === Intent.WARNING &&
    css`
      ${intentWarning}
    `}

  ${(props) =>
    props.$intent === Intent.DANGER &&
    css`
      ${intentDanger}
    `}
`;

export const HrefLink = styled.a.attrs(options)<
  IHrefLink & IntentProps & Active
>`
  ${base};
  ${(props) =>
    props.$active &&
    css`
      color: ${props.theme.colors.blueDark};
      &:link,
      &:visited,
      &:active {
        color: ${props.theme.colors.blueDark};
      }
    `}

  ${(props) =>
    props.$isDisabled &&
    css`
      color: ${props.theme.colors.lightGrey};
      cursor: not-allowed;
      pointer-events: none;
    `}
`;

export const BaseLink = styled(Link)<Color & IntentProps & Active>`
  ${base}

  ${(props) =>
    props.$color &&
    css`
      color: ${props.$color};
    `}
`;

export const NavLink = styled(BaseNavLink).attrs(options)<
  IHrefLink & IntentProps & Active
>`
  ${base};

  &.active {
    border-bottom: 1px solid ${(props) => props.theme.colors.blue};
  }
`;
